import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star, TrendingUp, Users, BookOpen } from 'lucide-react';

const CourseCard = React.memo(({ course }) => {
    const isLive = course.category === 'live';
    const isOffline = course.category === 'offline';
    const isSelfPaced = course.category === 'self-paced';
    const isFree = course.category === 'free';
    const BannerIcon = course.banner.Icon;
    
    // Check if this is the Complete C++ course
    const isCompleteCppCourse = course.slug === 'c-cpp-programming' || course.title === 'Complete C++ with OOPS & STL';
    
    // Check if this is the C & C++ Complete Course
    const isCCppCompleteCourse = course.slug === 'c-cpp-complete-offline' || course.title === 'C & C++ Complete Programming Course';

    return (
        <Link to={`/course/${course.slug || course.id}`} className="block h-full">
            <motion.div
                className={`card-premium interactive-hover ripple-effect flex flex-col overflow-hidden h-full group min-h-[520px] ${
                    isCompleteCppCourse 
                        ? 'ring-4 ring-primary-500/30 shadow-2xl shadow-primary-500/20 border-2 border-primary-300/50 animate-glow' 
                        : isCCppCompleteCourse
                        ? 'ring-4 ring-blue-500/30 shadow-2xl shadow-blue-500/20 border-2 border-blue-300/50 animate-glow'
                        : ''
                }`}
                whileHover={{ 
                    y: isCompleteCppCourse ? -8 : isCCppCompleteCourse ? -8 : -6, 
                    scale: isCompleteCppCourse ? 1.03 : isCCppCompleteCourse ? 1.03 : 1.02 
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
            >
                {/* Course Image/Header */}
                <div className="relative">
                    <div className={`h-44 ${course.banner.background} text-white p-6 relative overflow-hidden flex items-center rounded-t-2xl ${
                        isCompleteCppCourse ? 'bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600' : 
                        isCCppCompleteCourse ? 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600' : ''
                    }`}>
                        <div className={`absolute inset-0 transition-colors duration-300 ${
                            isCompleteCppCourse ? 'bg-gradient-to-r from-blue-500/20 to-indigo-500/20 group-hover:from-blue-500/30 group-hover:to-indigo-500/30' : 
                            isCCppCompleteCourse ? 'bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-purple-500/20 group-hover:from-blue-500/30 group-hover:via-indigo-500/30 group-hover:to-purple-500/30' : 
                            'bg-black/10 group-hover:bg-black/20'
                        }`}></div>
                        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/20"></div>
                        
                        <div className={`relative z-10 w-3/5${isLive || isOffline || isSelfPaced || isFree ? ' pt-8' : ''}`}>
                            <h3 className="text-xl font-bold tracking-wide leading-tight text-shadow-md">{course.banner.title}</h3>
                            {course.banner.subtitle && course.category !== 'live' && (
                                <div className="text-base font-semibold text-shadow-md mt-1">{course.banner.subtitle}</div>
                            )}
                        </div>

                        {BannerIcon && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 h-full w-2/5 flex items-center justify-center z-0 transform-gpu transition-transform duration-300 group-hover:scale-105 group-hover:-rotate-2">
                                <BannerIcon className="w-24 h-24 text-white/25" strokeWidth={1.2} />
                            </div>
                        )}
                    </div>
                    
                    {/* Course Category Badge */}
                    {isCompleteCppCourse && (
                        <div className="absolute top-4 left-4 z-20">
                            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse shadow-lg flex items-center border-2 border-white/50">
                                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                                🚀 FEATURED
                            </span>
                        </div>
                    )}
                    
                    {isLive && !isCompleteCppCourse && (
                        <div className="absolute top-4 left-4">
                            <span className="bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse shadow-lg flex items-center">
                                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                                LIVE
                            </span>
                        </div>
                    )}
                    
                    {isOffline && (
                        <div className="absolute top-4 left-4">
                            <span className="bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl flex items-center animate-pulse relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 animate-pulse"></div>
                                <span className="relative z-10 flex items-center">
                                    <span className="w-2 h-2 bg-white rounded-full mr-2 animate-ping"></span>
                                    <span className="font-extrabold tracking-wide">OFFLINE</span>
                                </span>
                            </span>
                        </div>
                    )}
                    
                    {isCCppCompleteCourse && (
                        <div className="absolute top-4 right-4">
                            <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-xl flex items-center animate-bounce relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-yellow-300/30 to-red-300/30 animate-pulse"></div>
                                <span className="relative z-10 flex items-center">
                                    <span className="mr-1 animate-spin">⭐</span>
                                    <span className="font-extrabold tracking-wide">FEATURED</span>
                                </span>
                            </span>
                        </div>
                    )}
                    
                    {isSelfPaced && (
                        <div className="absolute top-4 left-4">
                            <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center">
                                <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                                SELF PACED
                            </span>
                        </div>
                    )}
                    
                    {isFree && (
                        <div className="absolute top-4 left-4">
                            <span className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center">
                                <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                                FREE
                            </span>
                        </div>
                    )}
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                    {/* Interested Count and Rating */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                        {/* Removed interested count and rating */}
                    </div>

                    {/* Course Title */}
                    <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-3 leading-tight line-clamp-3 min-h-[4.5rem] group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
                        {course.title}
                    </h4>

                    {/* Course Details */}
                    <div className="space-y-2 mb-4 flex-grow">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <BookOpen className="w-4 h-4 mr-2 text-blue-500" />
                            <span>Level: {course.level}</span>
                        </div>
                        {/* Seats left indicator removed */}
                    </div>
                    
                    {/* Price and CTA */}
                    <div className="mt-auto">
                        {isLive ? (
                            // Live course specific content
                            <>
                                {isCompleteCppCourse ? (
                                    // Complete C++ course - show start date
                                    <>
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-baseline space-x-2">
                                                <span className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                                                    ₹{course.price}
                                                </span>
                                                <span className="text-sm text-gray-400 line-through">
                                                    ₹{course.originalPrice}
                                                </span>
                                                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full">
                                                    19% OFF
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 dark:from-green-900/30 dark:via-blue-900/30 dark:to-purple-900/30 border-2 border-green-400 dark:border-green-600 rounded-xl shadow-2xl relative overflow-hidden animate-glow">
                                            <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 via-blue-400/20 to-purple-400/20 animate-rainbow"></div>
                                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 animate-pulse"></div>
                                            <div className="relative z-10">
                                                <div className="text-sm font-bold text-green-700 dark:text-green-300 mb-2 flex items-center">
                                                    <span className="animate-bounce mr-2">🚀</span>
                                                    <span className="ml-2 bg-gradient-to-r from-green-500 to-blue-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse-glow">
                                                        FEATURED
                                                    </span>
                                                </div>
                                                <div className="text-sm font-semibold text-green-800 dark:text-green-200 bg-white/60 dark:bg-black/40 px-3 py-2 rounded-lg border border-green-400 dark:border-green-500 shadow-lg animate-float">
                                                    📅 Batch starts from 23-08-2025
                                                </div>
                                            </div>
                                        </div>
                                        <button className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl border-2 border-primary-500/50">
                                            🎯 Enroll Now - Limited Seats!
                                        </button>
                                    </>
                                ) : (
                                    // Other live courses - show start date
                                    <>
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-baseline space-x-2">
                                                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    ₹{course.price}
                                                </span>
                                                <span className="text-sm text-gray-400 line-through">
                                                    ₹{course.originalPrice}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/30 dark:to-red-900/30 border-2 border-orange-300 dark:border-orange-700 rounded-xl shadow-lg relative overflow-hidden animate-glow">
                                            <div className="absolute inset-0 bg-gradient-to-r from-orange-400/10 to-red-400/10 animate-shimmer"></div>
                                            <div className="relative z-10">
                                                <div className="text-sm font-bold text-orange-700 dark:text-orange-300 mb-2 flex items-center">
                                                    <span className="animate-bounce mr-2">🚀</span>
                                                    Starting Soon
                                                    <span className="ml-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse-glow">
                                                        HOT
                                                    </span>
                                                </div>
                                                <div className="text-sm font-semibold text-orange-800 dark:text-orange-200 bg-white/50 dark:bg-black/30 px-3 py-2 rounded-lg border border-orange-300 dark:border-orange-600 animate-float">
                                                    📅 Batch starts from {course.startDate ? new Date(course.startDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'TBA'}
                                                </div>
                                            </div>
                                        </div>
                                        <button className="w-full btn-brand-glow btn-md">
                                            Enroll Now
                                        </button>
                                    </>
                                )}
                            </>
                        ) : (
                            // Non-live courses - original logic
                            course.price ? (
                                <>
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-baseline space-x-2">
                                            <span className="text-2xl font-bold text-gray-900 dark:text-white">
                                                ₹{course.price}
                                            </span>
                                            <span className="text-sm text-gray-400 line-through">
                                                ₹{course.originalPrice}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Show start date for offline classes if available */}
                                    {isOffline && course.startDate && (
                                        <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-purple-900/30 border-2 border-blue-300 dark:border-blue-600 rounded-xl shadow-lg relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 via-indigo-400/10 to-purple-400/10 animate-pulse"></div>
                                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
                                            <div className="relative z-10">
                                                <div className="text-sm font-bold text-blue-700 dark:text-blue-300 mb-2 flex items-center justify-center">
                                                    <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs px-4 py-2 rounded-full font-bold shadow-lg animate-pulse flex items-center">
                                                        <span className="animate-bounce mr-2 text-lg">🚀</span>
                                                        <span className="w-2 h-2 bg-white rounded-full mr-2 animate-ping"></span>
                                                        NEW BATCH
                                                    </span>
                                                </div>
                                                <div className="text-xl font-extrabold text-blue-800 dark:text-blue-200 bg-gradient-to-r from-white/80 to-blue-50/80 dark:from-black/60 dark:to-blue-900/60 px-6 py-4 rounded-xl border-2 border-blue-400 dark:border-blue-500 shadow-xl relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-indigo-400/10 animate-pulse"></div>
                                                    <div className="relative z-10 flex items-center justify-center">
                                                        <span className="mr-2 text-2xl animate-bounce">📅</span>
                                                        <span className="tracking-wider">
                                                            {new Date(course.startDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl border-2 border-blue-500/50 relative overflow-hidden group">
                                        <span className="relative z-10 flex items-center justify-center">
                                            <span className="mr-2">🎯</span>
                                            Enroll Now - Limited Seats!
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-indigo-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    </button>
                                </>
                            ) : (
                                <button className="w-full btn-brand-glow btn-md">
                                    Explore Course
                                </button>
                            )
                        )}
                    </div>

                    {/* IBM Badge */}
                    {course.isIbm && (
                        <div className="mt-3 flex justify-end">
                            <div className="flex items-center space-x-1">
                                <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
                                    <span className="text-white text-xs font-bold">IBM</span>
                                </div>
                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Certified</span>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </Link>
    );
});

export default CourseCard; 